/**
 * PlatformFooter - global ink-900 (#131314) footer with quick-links.
 *
 * Mounted globally inside the Router so it knows the current path.
 * Hidden automatically on full-screen flows (ceremony sessions, auth, embed
 * iframes, the public snapshot view) where chrome would be intrusive.
 */
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, BookOpen, Search, Vault, Network, HelpCircle, Mail, ExternalLink, GraduationCap, NotebookPen, ShieldCheck, Handshake, Activity, Scale, MapPin } from 'lucide-react';
import { isChromeHidden } from '../lib/chromeVisibility';
import { isParked } from '../config/parkedFeatures';
import ContactSalesModal from './ContactSalesModal';

const QUICK_LINKS = [
  { label: 'Verify a document', to: '/verify', icon: Search },
  { label: 'Marketplace', to: '/marketplace', icon: Network },
  { label: 'Trust Hub', to: '/trust-hub', icon: Network },
  { label: 'Asset Vault', to: '/asset-vault', icon: Vault },
  { label: 'User Guide', to: '/docs', icon: BookOpen },
  { label: 'State Compliance', to: '/compliance/states', icon: Shield },
  { label: 'Smart Journal', to: '/journal', icon: NotebookPen },
  { label: 'NotaryGuard', to: '/notaryguard', icon: ShieldCheck },
  { label: 'FL RON Academy', to: '/academy', icon: GraduationCap },
  { label: 'FL Training Manual', to: '/training/florida-manual', icon: GraduationCap },
  { label: 'FL Registry Check', to: '/registry/fl', icon: GraduationCap },
  { label: 'Partner Program', to: '/partners', icon: Handshake },
  { label: 'Notaries Wanted', to: '/notaries-wanted', icon: MapPin },
  { label: 'Hedera Analytics', to: '/hedera-analytics', icon: Activity },
  { label: 'Fraud Shield', to: '/fraud-shield', icon: ShieldCheck },
  { label: 'Litigation Packets', to: '/litigation-packets', icon: Scale },
  { label: 'Contractor Escrow', to: '/contractor', icon: Handshake },
  { label: 'Vessel & Aircraft', to: '/vessel-aircraft', icon: ShieldCheck },
  { label: 'e-Apostille', to: '/apostille', icon: Network },
];

const RESOURCE_LINKS = [
  { label: 'About', to: '/' },
  { label: 'For Investors', to: '/investors' },
  { label: 'Trust Center', to: '/security' },
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
  { label: 'Pricing', to: '/pricing' },
];

export default function PlatformFooter() {
  const { pathname } = useLocation();
  const [salesOpen, setSalesOpen] = useState(false);
  if (isChromeHidden(pathname)) return null;

  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#131314] text-cream-100 mt-20" data-testid="platform-footer">
      {/* Enterprise Contact Sales CTA (shared across all public pages) */}
      <div className="border-b border-ink-700">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-lg font-bold text-white">Notarizing at scale?</p>
            <p className="text-sm text-ink-200">Enterprise RON, escrow &amp; compliance for title, legal, and lending teams.</p>
          </div>
          <button onClick={() => setSalesOpen(true)} data-testid="contact-sales-cta"
            className="inline-flex items-center gap-2 rounded-full bg-coral-500 hover:bg-coral-600 text-white font-semibold px-6 py-2.5 transition-colors">
            <Handshake className="w-4 h-4" /> Contact Sales
          </button>
        </div>
      </div>
      <ContactSalesModal open={salesOpen} onClose={() => setSalesOpen(false)} />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Brand column */}
          <div className="md:col-span-4" data-testid="footer-brand">
            <Link to="/" className="inline-flex items-center gap-2 mb-3">
              <img src="/logo.png" alt="NotaryChain seal" className="w-7 h-7 rounded-full" />
              <span className="font-bold text-lg">Notary<span className="text-coral-500">Chain</span></span>
            </Link>
            <p className="text-ink-200 text-sm leading-relaxed max-w-xs">
              Remote Online Notarization with Hedera-backed seals, multi-state compliance
              evaluators, and federated TrustLayer attestations.
            </p>
            <div className="mt-4 flex items-center gap-3 text-sm">
              <a href="mailto:support@notarychain.app" className="inline-flex items-center gap-1.5 text-cream-100 hover:text-coral-400 transition-colors" data-testid="footer-support-email">
                <Mail className="w-4 h-4" /> support@notarychain.app
              </a>
            </div>
          </div>

          {/* Quick actions column */}
          <div className="md:col-span-5" data-testid="footer-quick-links">
            <p className="text-[11px] uppercase tracking-[0.2em] text-ink-300 font-bold mb-4">Quick actions</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
              {QUICK_LINKS.filter(({ to }) => !isParked(to)).map(({ label, to, icon: Icon }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="inline-flex items-center gap-2 text-sm text-cream-100 hover:text-coral-400 transition-colors group"
                    data-testid={`footer-link-${to.replace(/\//g, '-').replace(/^-/, '')}`}
                  >
                    <Icon className="w-3.5 h-3.5 text-ink-300 group-hover:text-coral-400 transition-colors" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources column */}
          <div className="md:col-span-3" data-testid="footer-resources">
            <p className="text-[11px] uppercase tracking-[0.2em] text-ink-300 font-bold mb-4">Resources</p>
            <ul className="space-y-2">
              {RESOURCE_LINKS.map(({ label, to }) => (
                <li key={to}>
                  <Link to={to} className="text-sm text-cream-100 hover:text-coral-400 transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/help" className="text-sm text-cream-100 hover:text-coral-400 transition-colors inline-flex items-center gap-1">
                  Help & FAQ <HelpCircle className="w-3 h-3" />
                </Link>
              </li>
              <li>
                <a
                  href="https://hashscan.io/mainnet/topic/0.0.10373605"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-cream-100 hover:text-coral-400 transition-colors inline-flex items-center gap-1"
                  data-testid="footer-hashscan-link"
                >
                  Hedera ledger <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-ink-700 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-[12px] text-ink-300" data-testid="footer-copyright">
            © {year} NotaryChain. Notarization, done right. Online, in minutes.
          </p>
          <div className="flex items-center gap-4 text-[12px] text-ink-300">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              All systems operational
            </span>
            <span className="opacity-50">·</span>
            <span>Hedera mainnet · Topic 0.0.10373605</span>
            <span className="opacity-50">·</span>
            <span data-testid="footer-build-version" title="Build footprint">Build 2026-05-27 · v3</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
